import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import '../styles/components/SearchList.css';
import SearchFilterControl from './SearchFilterControl';
import { FilterAction } from './FilterBar';
import PaginationUI from './PaginationUI';
import { downloadCsv, buildCsvFromColumns } from '../utils/csvUtils';
import { exportTableAsPdf } from '../utils/pdfExport';
import UploadModal from './common/UploadModal';

// Generic interfaces for the reusable component
export interface SearchListColumn {
  key: string;
  header: string;
  subtext?: string | React.ReactNode;
  sortable?: boolean;
  width?: string;
  allowOverflow?: boolean;  // Add this property
  render: (item: any, index: number) => React.ReactNode;
  tooltip?: (item: any) => string;
}

export interface SearchListConfig {
  columns: SearchListColumn[];
  filterConfig: any;
  searchPlaceholder?: string;
  showSearch?: boolean;
  showFilterButton?: boolean;
  showFilterBar?: boolean;
  showCreateButton?: boolean;
  showBuildUpload?: boolean;
  itemsPerPage?: number;
  defaultSort?: {
    field: string;
    direction: 'asc' | 'desc';
  };
  exportConfig?: {
    enabled: boolean;
    filenameBase?: string; // default: searchlist
    columns?: Array<{ header: string; value: (item: any) => string }>; // if omitted, use visible columns
    // PDF-specific configuration
    pdfColumnWidths?: Record<string, string>; // Override column widths for PDF (e.g., { 'prompt': '3.3fr', 'id': '0.7fr' })
  };
  uploadConfig?: {
    instructions?: React.ReactNode;
    acceptedFileTypes?: string[];
    title?: string;
    fileLabel?: string;
    fileTypeDescription?: string;
    filenamePattern?: RegExp;
    filenamePatternHelpText?: string;
  };
}

export interface SearchListProps {
  config: SearchListConfig;
  data: any[];
  loading: boolean;
  error: string | null;
  filters: Record<string, any>;
  searchQuery: string;
  currentPage: number;
  totalPages: number;
  itemsPerPage: number;
  totalItems?: number;
  sortConfig: {
    field: string;
    direction: 'asc' | 'desc';
  };
  onSearchChange: (value: string) => void;
  onSearchSubmit: (e: React.FormEvent) => void;
  onFilterChange: (filters: Record<string, any>) => void;
  onFilterActionChange: (action: FilterAction) => void;
  onPageChange: (page: number) => void;
  onSort: (field: string) => void;
  onItemClick?: (item: any) => void;
  onCreateClick?: () => void;
  onUpload?: (file: File, updateExisting: boolean) => Promise<{
    message?: string;
    data: {
      inserted_count?: number;
      updated_count?: number;
      skipped_count?: number;
      failure_count?: number;
      errors?: Array<{ row: number; data: any; error: string }>;
    };
  }>;
  onUploadSuccess?: () => void;
  emptyMessage?: string;
  loadingMessage?: string;
  notes?: (item: any) => string | null;
  footer?: (item: any) => React.ReactNode;
  getRowClassName?: (item: any) => string;
  fetchAllResults?: (args: { filters: Record<string, any>; sort: { field: string; direction: 'asc' | 'desc' } }) => Promise<any[]>;
}

const SearchList: React.FC<SearchListProps> = ({
  config,
  data,
  loading,
  error,
  filters,
  searchQuery,
  currentPage,
  totalPages,
  itemsPerPage,
  totalItems,
  sortConfig,
  onSearchChange,
  onSearchSubmit,
  onFilterChange,
  onFilterActionChange,
  onPageChange,
  onSort,
  onItemClick,
  onCreateClick,
  onUpload,
  onUploadSuccess,
  emptyMessage = "No items found",
  loadingMessage = "Loading...",
  notes,
  footer,
  getRowClassName,
  fetchAllResults
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [exportMenuOpen, setExportMenuOpen] = useState(false);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const tableContainerRef = useRef<HTMLDivElement>(null);

  // Common filter action handling logic
  const handleFilterActionChange = (action: FilterAction) => {
    if (action.type === 'REMOVE') {
      const newFilters = {...filters};
      
      // Handle array values (like transcript_keywords)
      if (action.value && Array.isArray(newFilters[action.key])) {
        newFilters[action.key] = newFilters[action.key].filter(
          (item: any) => item !== action.value
        );
        
        // Remove the key if array is empty
        if (newFilters[action.key].length === 0) {
          delete newFilters[action.key];
        }
      } else {
        // Handle non-array values
        delete newFilters[action.key];
      }
      
      // Call parent's onFilterChange with updated filters
      onFilterChange(newFilters);
    } 
    else if (action.type === 'EDIT') {
      // Handle EDIT action for filter tag updates
      const newFilters = {...filters};
      
      // Handle array values
      if (Array.isArray(newFilters[action.key])) {
        // Find and replace the specific value in the array
        newFilters[action.key] = newFilters[action.key].map((item: any) => 
          item === action.oldValue ? action.newValue : item
        );
      } else {
        // Handle simple values
        newFilters[action.key] = action.newValue;
      }
      
      // Call parent's onFilterChange with updated filters
      onFilterChange(newFilters);
    }
  };

  // Render sort indicators
  const renderSortIndicator = (field: string, column: SearchListColumn) => {
    if (!column.sortable) return null;
    
    const isActive = sortConfig.field === field;
    const isAsc = isActive && sortConfig.direction === 'asc';
    const isDesc = isActive && sortConfig.direction === 'desc';
    
    return (
      <div className="sort-indicator-container">
        <span className={`sort-indicator sort-indicator-up ${isAsc ? 'sort-indicator-active' : ''}`}>
          ↑
        </span>
        <span className={`sort-indicator sort-indicator-down ${isDesc ? 'sort-indicator-active' : ''}`}>
          ↓
        </span>
      </div>
    );
  };

  // Generate grid template columns based on column widths
  const getGridTemplateColumns = () => {
    return config.columns.map(col => col.width || '1fr').join(' ');
  };

  // Show loading state
  if (loading) {
    return <div className="search-list-loading">{loadingMessage}</div>;
  }

  // Show error state
  if (error) {
    return <div className="search-list-error">{error}</div>;
  }

  const getDefaultExportColumns = (): Array<{ header: string; value: (item: any) => string }> => {
    return config.columns.map(col => ({
      header: col.header,
      value: (item: any) => {
        const rendered = col.render(item, 0) as any;
        if (typeof rendered === 'string' || typeof rendered === 'number') return String(rendered);
        if (item && col.key in item && (typeof item[col.key] === 'string' || typeof item[col.key] === 'number')) {
          return String(item[col.key]);
        }
        return '';
      }
    }));
  };

  // Shared function to export CSV data
  const exportToCsv = (rows: any[], filenameBase?: string) => {
    const cols = config.exportConfig?.columns || getDefaultExportColumns();
    const rowsForCsv = buildCsvFromColumns(rows, cols);
    downloadCsv(rowsForCsv, filenameBase || config.exportConfig?.filenameBase || 'searchlist');
  };

  // Helper function to get filter label
  const getFilterLabel = (key: string): string => {
    const filterDef = config.filterConfig?.filters?.find((f: any) => f.key === key);
    return filterDef?.label || key;
  };

  // Render filter tags for PDF (without buttons)
  const renderFilterTagsForPDF = () => {
    const tags: string[] = [];
    Object.entries(filters).forEach(([key, value]) => {
      const displayLabel = getFilterLabel(key);
      if (Array.isArray(value)) {
        value.forEach(item => {
          tags.push(`${displayLabel}: ${item}`);
        });
      } else if (value !== undefined && value !== null && value !== '') {
        tags.push(`${displayLabel}: ${value}`);
      }
    });
    return tags;
  };


  // Handle PDF export for current page
  const handleExportPDF = () => {
    if (!tableContainerRef.current || data.length === 0) return;

    const ts = new Date();
    const pad = (n: number) => String(n).padStart(2, '0');
    const filename = `${config.exportConfig?.filenameBase || 'searchlist'}-page-${currentPage}-${ts.getFullYear()}${pad(ts.getMonth() + 1)}${pad(ts.getDate())}-${pad(ts.getHours())}${pad(ts.getMinutes())}.pdf`;

    const filterTags = renderFilterTagsForPDF();
    const startItem = (currentPage - 1) * itemsPerPage + 1;
    const endItem = Math.min(currentPage * itemsPerPage, totalItems || data.length);
    const pageInfoText = `Page ${currentPage} of ${totalPages} (Items ${startItem}-${endItem}${totalItems ? ` of ${totalItems}` : ''})`;

    const pdfColumnWidths = config.exportConfig?.pdfColumnWidths || {};
    const gridTemplateColumns = config.columns
      .map(col => pdfColumnWidths[col.key] || col.width || '1fr')
      .join(' ');

    exportTableAsPdf({
      container: tableContainerRef.current,
      filename,
      gridTemplateColumns,
      headerSelector: '.search-list-header-grid',
      rowSelector: '.search-list-item',
      wrapperSelector: '.search-list-item-wrapper',
      contentContainerSelector: '.search-list-container',
      removeSelectors: ['.pagination-ui', '.pagination-wrapper', '.footer-actions'],
      filterTags,
      pageInfoText
    });
  };

  return (
    <div className="search-list-screen">
      <div className="search-list-header">
        <div className="search-list-header-left">
          {config.showCreateButton && (
            <div className="create-button-header">
              <button 
                className="search-list-create-button"
                onClick={onCreateClick}
                disabled={loading}
                title="Create New"
              >
                <span className="create-button-icon">+</span>
              </button>
            </div>
          )}
          {config.showBuildUpload && (
            <div className="create-button-header">
              <button 
                className="search-list-create-button search-list-upload-button"
                onClick={() => setUploadModalOpen(true)}
                disabled={loading}
                title="Upload CSV"
              >
                <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>upload</span>
              </button>
            </div>
          )}
          <SearchFilterControl
            searchQuery={searchQuery}
            filters={filters}
            filterConfig={config.filterConfig}
            showSearch={config.showSearch ?? true}
            showFilterButton={config.showFilterButton ?? true}
            showFilterBar={config.showFilterBar ?? true}
            searchPlaceholder={config.searchPlaceholder || "Search"}
            onSearchChange={onSearchChange}
            onSearchSubmit={onSearchSubmit}
            onFilterChange={onFilterChange}
            onFilterActionChange={handleFilterActionChange}
          />
        </div>
        {config.exportConfig?.enabled && (
          <div className="search-list-header-right">
            <div className={`export-menu ${exportMenuOpen ? 'open' : ''}`}>
              <button
                type="button"
                className="export-button"
                onClick={() => setExportMenuOpen(v => !v)}
                title="Import Results"
                aria-label="Import Results"
              >
                <span className="material-symbols-outlined">download</span>
                {/* <span className="caret">▾</span> */}
              </button>
              {exportMenuOpen && (
                <div className="export-dropdown">
                  <div className="export-dropdown-section">
                    <div className="export-dropdown-section-title">Current page</div>
                    <button 
                      type="button" 
                      className="export-dropdown-item" 
                      onClick={() => { 
                        setExportMenuOpen(false); 
                        exportToCsv(data); 
                      }}
                    >
                      <span className="material-symbols-outlined" style={{ fontSize: '18px', marginRight: '8px' }}>file_download</span>
                      <span>CSV</span>
                    </button>
                    <button type="button" className="export-dropdown-item" onClick={() => { setExportMenuOpen(false); handleExportPDF(); }}>
                      <span className="material-symbols-outlined" style={{ fontSize: '18px', marginRight: '8px' }}>picture_as_pdf</span>
                      <span>PDF</span>
                    </button>
                  </div>
                  {fetchAllResults && (
                    <div className="export-dropdown-section">
                      <div className="export-dropdown-section-title">All results</div>
                      <button 
                        type="button" 
                        className="export-dropdown-item" 
                        onClick={async () => { 
                          setExportMenuOpen(false); 
                          if (fetchAllResults) {
                            const rows = await fetchAllResults({ filters, sort: sortConfig });
                            exportToCsv(rows);
                          }
                        }}
                      >
                        <span className="material-symbols-outlined" style={{ fontSize: '18px', marginRight: '8px' }}>file_download</span>
                        <span>CSV</span>
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      
      <div className="search-list-container" ref={tableContainerRef}>
        <div 
          className="search-list-header-grid"
          style={{ gridTemplateColumns: getGridTemplateColumns() }}
        >
          {config.columns.map((column) => (
            <div 
              key={column.key}
              onClick={() => column.sortable && onSort(column.key)}
              className={`search-list-header-cell ${column.sortable ? 'sortable' : 'non-sortable'}`}
            >
              {column.sortable && renderSortIndicator(column.key, column)}
              <div className="header-content">
                <span className="header-text">{column.header}</span>
                {column.subtext && <div className="header-subtext">{column.subtext}</div>}
              </div>
            </div>
          ))}
        </div>
        
        {data.length === 0 ? (
          <div className="search-list-empty">{emptyMessage}</div>
        ) : (
          <>
            <div className="search-list-items">
              {data.map((item, index) => (
                <div key={index} className={`search-list-item-wrapper ${getRowClassName ? getRowClassName(item) : ''}`}>
                  <div 
                    className={`search-list-item ${onItemClick ? 'clickable' : ''} ${config.columns.some(col => col.allowOverflow) ? 'allow-cell-overflow' : ''}`}
                    onClick={() => onItemClick && onItemClick(item)}
                    style={{ gridTemplateColumns: getGridTemplateColumns() }}
                  >
                    {config.columns.map((column) => {
                      const renderedContent = column.render(item, index);
                      const isReactElement = React.isValidElement(renderedContent);
                      
                      return (
                        <div key={column.key} className={`list-item-cell ${column.allowOverflow ? 'allow-cell-overflow' : ''}`}>
                          {isReactElement ? (
                            // For React components, render directly without wrapper
                            <div className={`list-item-component ${column.allowOverflow ? 'allow-cell-overflow' : ''}`}>
                              {renderedContent}
                            </div>
                          ) : (
                            // For text content, use the wrapper with tooltip
                            <div 
                              className="list-item-text"
                              title={column.tooltip ? column.tooltip(item) : undefined}
                            >
                              {renderedContent}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  {notes && notes(item) && (
                    <div className="search-list-item-note">
                      {notes(item)}
                    </div>
                  )}
                  {footer && (
                    <div className="search-list-item-footer">
                      {footer(item)}
                    </div>
                  )}
                </div>
              ))}
            </div>
            
            {/* Pagination controls */}
            <PaginationUI
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={onPageChange}
              variant="default"
              disabled={loading}
              totalItems={totalItems}
              itemsPerPage={itemsPerPage}
              showItemRange={true}
              showPageNumbers={true}
            />
          </>
        )}
      </div>

      {/* Upload Modal */}
      {onUpload && (
        <UploadModal
          isOpen={uploadModalOpen}
          onClose={() => setUploadModalOpen(false)}
          onUpload={onUpload}
          onUploadSuccess={onUploadSuccess}
          filenameBase={config.exportConfig?.filenameBase || 'searchlist'}
          instructions={config.uploadConfig?.instructions}
          acceptedFileTypes={config.uploadConfig?.acceptedFileTypes}
          modalTitle={config.uploadConfig?.title}
          fileLabel={config.uploadConfig?.fileLabel}
          fileTypeDescription={config.uploadConfig?.fileTypeDescription}
          filenamePattern={config.uploadConfig?.filenamePattern}
          filenamePatternHelpText={config.uploadConfig?.filenamePatternHelpText}
        />
      )}
    </div>
  );
};

export default SearchList; 